import { useState, useEffect, useCallback } from 'react';
import { dashboardAPI, retainersAPI } from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { FolderKanban, FileText, DollarSign, TrendingUp, CheckCircle, Calendar, AlertTriangle, CalendarClock, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const Dashboard = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [retainerStats, setRetainerStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const dashRes = await dashboardAPI.getStats();
      setStats(dashRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
      toast.error('Failed to fetch dashboard stats');
    }
    try {
      const retRes = await retainersAPI.getDashboardStats();
      setRetainerStats(retRes.data);
    } catch (error) {
      // Retainer stats are optional — don't break the dashboard
      console.error('Failed to fetch retainer stats:', error);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loading) {
    return <div className="text-center py-12">Loading dashboard...</div>;
  }

  if (!stats) return null;

  // Prepare segment data for chart
  const segmentChartData = stats.segment_totals 
    ? Object.entries(stats.segment_totals)
        .filter(([_, value]) => value > 0)
        .map(([name, value]) => ({ name, value }))
    : [];

  const SEGMENT_COLORS = [
    '#2563EB', '#7C3AED', '#DC2626', '#059669', '#D97706', '#EC4899', '#6366F1', '#14B8A6'
  ];

  const statCards = [
    {
      title: 'Ongoing Projects',
      value: stats.ongoing_projects,
      icon: FolderKanban,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
    },
    {
      title: 'Draft Sales Orders',
      value: stats.draft_so,
      icon: FileText,
      color: 'text-gray-600',
      bgColor: 'bg-gray-50',
    },
    {
      title: 'Approved Sales Orders',
      value: stats.approved_so,
      icon: TrendingUp,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      title: 'Invoice Raised',
      value: stats.invoice_raised_so,
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      title: 'Invoice Raised Value (YTD)',
      value: `₹${(stats.invoice_raised_ytd || 0).toLocaleString('en-IN')}`,
      icon: DollarSign,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
    },
    {
      title: 'Invoice Raised Value (MTD)',
      value: `₹${(stats.invoice_raised_mtd || 0).toLocaleString('en-IN')}`,
      icon: Calendar,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
  ];

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="dashboard">
      <div>
        <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Dashboard
        </h1>
        <p className="text-muted-foreground mt-2">
          Welcome back, {user?.name}! Here's your overview.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title} className="stat-card" data-testid={`stat-card-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {stat.title}
                </CardTitle>
                <div className={`${stat.bgColor} p-2 rounded-md`}>
                  <Icon className={`h-4 w-4 ${stat.color}`} strokeWidth={1.5} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {stat.value}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Retainer Stats */}
      {retainerStats && (
        <>
          <div>
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Retainers</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="stat-card" data-testid="stat-card-retainers-due">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Retainers Due This Month</CardTitle>
                  <div className="bg-orange-50 p-2 rounded-md"><CalendarClock className="h-4 w-4 text-orange-600" strokeWidth={1.5} /></div>
                </CardHeader>
                <CardContent><div className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>{retainerStats.due_this_month}</div></CardContent>
              </Card>
              <Card className="stat-card" data-testid="stat-card-overdue-retainers">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Overdue Retainers</CardTitle>
                  <div className="bg-red-50 p-2 rounded-md"><AlertTriangle className="h-4 w-4 text-red-600" strokeWidth={1.5} /></div>
                </CardHeader>
                <CardContent><div className="text-3xl font-bold text-red-600" style={{ fontFamily: 'Manrope, sans-serif' }}>{retainerStats.overdue_retainers}</div></CardContent>
              </Card>
              <Card className="stat-card" data-testid="stat-card-retainer-revenue">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Retainer Revenue (MTD)</CardTitle>
                  <div className="bg-teal-50 p-2 rounded-md"><RefreshCw className="h-4 w-4 text-teal-600" strokeWidth={1.5} /></div>
                </CardHeader>
                <CardContent><div className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>{`\u20B9${(retainerStats.retainer_revenue_mtd || 0).toLocaleString('en-IN')}`}</div></CardContent>
              </Card>
            </div>
          </div>
        </>
      )}

      {/* Segment-wise Sales Order Value Chart - full width */}
      <Card data-testid="segment-chart-card">
        <CardHeader>
          <CardTitle className="text-xl font-semibold">Sales Order Value by Segment</CardTitle>
        </CardHeader>
        <CardContent>
          {segmentChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={Math.max(320, segmentChartData.length * 55)}>
              <BarChart
                data={segmentChartData}
                layout="vertical"
                margin={{ top: 8, right: 40, left: 24, bottom: 8 }}
                barCategoryGap="30%"
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}K`}
                />
                <YAxis
                  dataKey="name"
                  type="category"
                  tick={{ fill: '#0f172a', fontSize: 13, fontWeight: 500 }}
                  width={140}
                  interval={0}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                  }}
                  formatter={(value) => [`₹${value.toLocaleString('en-IN')}`, 'Value']}
                />
                <Bar dataKey="value" fill="#2563EB" radius={[0, 4, 4, 0]} barSize={22} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No sales order data available by segment
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Projects - full width */}
      <Card data-testid="recent-projects-card">
        <CardHeader>
          <CardTitle className="text-xl font-semibold">Recent Projects</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {stats.recent_projects?.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No projects yet</p>
            ) : (
              stats.recent_projects?.map((project) => (
                <div
                  key={project.id}
                  className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-accent transition-colors"
                  data-testid={`recent-project-${project.id}`}
                >
                  <div>
                    <p className="font-medium">{project.name}</p>
                    <p className="text-sm text-muted-foreground">{project.client_name}</p>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      project.status === 'ongoing'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-green-100 text-green-700'
                    }`}
                  >
                    {project.status}
                  </span>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
